/**
 * Dispute Service — file + respond to rental disputes.
 *
 * Uses the rental_disputes table + a `disputes` storage bucket for
 * evidence photos. Each photo is compressed via the shared
 * image-compress helper before upload. URL + capture-source metadata
 * are written in lockstep — the CHECK constraint from migration
 * 20260429000009 enforces 1:1 length parity.
 *
 * Writes to rental_disputes kick off a notification via the existing
 * push-notification DB trigger; the audit_logs row lands via
 * 20260429000007's trigger.
 */
import * as Crypto from 'expo-crypto'
import { supabase } from '../supabase'
import { jsonObject, jsonString } from '../types/db-rows'
import type { Json, Row } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { compressAndUpload } from '../utils/image-compress'
import { signedUrls } from '../utils/storage-url'
import { requireLiveUserId } from './session-identity'
import type {
  DisputeEvidenceInput,
  EvidenceMetadata,
  FileDisputeInput,
  RentalDispute,
  RespondToDisputeInput,
} from '../types/dispute.types'

const BUCKET = 'disputes'
const RESPONSE_WINDOW_HOURS = 72

type DisputeRow = Row<'rental_disputes'>

/**
 * `EvidenceMetadata` is a closed domain shape, while the generated `Json`
 * type's object branch demands an open `{ [key: string]: Json | undefined }`
 * index signature. Rebuilding every entry as a fresh object literal satisfies
 * that index signature structurally, so the JSONB column accepts the payload
 * with no cast at all.
 */
function toEvidenceJson(metadata: EvidenceMetadata[]): Json {
  return metadata.map((entry) => ({
    captureSource: entry.captureSource,
    capturedAtClient: entry.capturedAtClient,
    uploadedAt: entry.uploadedAt,
  }))
}

/**
 * The column is `jsonb`, so its contents are whatever a past app version (or
 * a backfill) wrote. Parse defensively instead of asserting the shape.
 */
function asEvidenceMetadataArray(value: Json): EvidenceMetadata[] {
  if (!Array.isArray(value)) return []
  return value.map((entry) => {
    const fields = jsonObject(entry)
    return {
      captureSource: fields.captureSource === 'camera' ? 'camera' : 'library',
      capturedAtClient: jsonString(fields.capturedAtClient) ?? '',
      uploadedAt: jsonString(fields.uploadedAt) ?? '',
    }
  })
}

function mapDispute(row: DisputeRow): RentalDispute {
  return {
    id: row.id,
    rentalId: row.rental_id,
    claimantId: row.claimant_id,
    respondentId: row.respondent_id,
    category: row.category,
    claimDescription: row.claim_description,
    claimAmountCents: row.claim_amount_cents,
    claimEvidenceUrls: row.claim_evidence_urls ?? [],
    claimEvidenceMetadata: asEvidenceMetadataArray(row.claim_evidence_metadata),
    status: row.status,
    responseDescription: row.response_description,
    responseEvidenceUrls: row.response_evidence_urls ?? [],
    responseEvidenceMetadata: asEvidenceMetadataArray(row.response_evidence_metadata),
    respondedAt: row.responded_at,
    resolutionNotes: row.resolution_notes,
    resolvedAmountCents: row.resolved_amount_cents,
    resolvedInFavorOf: row.resolved_in_favor_of,
    resolvedBy: row.resolved_by,
    resolvedAt: row.resolved_at,
    responseDeadline: row.response_deadline,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/** Evidence lives in the private `disputes` bucket, so viewers need signed URLs. */
async function withSignedEvidence(dispute: RentalDispute): Promise<RentalDispute> {
  return {
    ...dispute,
    claimEvidenceUrls: await signedUrls(BUCKET, dispute.claimEvidenceUrls),
    responseEvidenceUrls: await signedUrls(BUCKET, dispute.responseEvidenceUrls),
  }
}

/**
 * Uploads each evidence item and returns parallel arrays of (a) the
 * resolved bucket URLs and (b) the per-item metadata that will be
 * written to the JSONB column. The CHECK constraint on
 * rental_disputes enforces `urls.length === metadata.length` so we
 * MUST emit them in lockstep — no try/catch swallowing partial
 * failures.
 */
async function uploadEvidenceWithMetadata(
  evidence: DisputeEvidenceInput[],
  pathPrefix: string,
): Promise<{ urls: string[]; metadata: EvidenceMetadata[] }> {
  const urls: string[] = []
  const metadata: EvidenceMetadata[] = []
  for (const item of evidence) {
    const path = `${pathPrefix}/${Crypto.randomUUID()}.jpg`
    const url = await compressAndUpload(item.uri, BUCKET, path, 'listing')
    const uploadedAt = new Date().toISOString()
    urls.push(url)
    metadata.push({
      captureSource: item.captureSource,
      capturedAtClient: item.capturedAtClient,
      uploadedAt,
    })
  }
  return { urls, metadata }
}

class DisputeServiceImpl {
  async fileDispute(userId: string, input: FileDisputeInput): Promise<RentalDispute> {
    // The claimant is whoever is filing — resolve that from the LIVE session,
    // not a hook-cached user id. Everything identity-bearing (the respondent
    // resolution, the evidence storage folder, and claimant_id which RLS checks
    // against auth.uid()) is derived from this live id so a drifted cache can't
    // mis-target the write and trip "new row violates row-level security policy".
    const liveId = await requireLiveUserId()
    void userId

    const { data: rental, error: rentalErr } = await supabase
      .from('rentals')
      .select('id, guest_id, host_id, status')
      .eq('id', input.rentalId)
      .single()

    if (rentalErr || !rental) throw ServiceError.notFound('Rental')

    const respondentId =
      rental.guest_id === liveId
        ? rental.host_id
        : rental.host_id === liveId
          ? rental.guest_id
          : null
    if (!respondentId) {
      throw ServiceError.unauthorized('Only rental participants can file disputes')
    }

    const { urls, metadata } = await uploadEvidenceWithMetadata(
      input.evidence,
      `${liveId}/${input.rentalId}`,
    )

    const responseDeadline = new Date(
      Date.now() + RESPONSE_WINDOW_HOURS * 60 * 60 * 1000,
    ).toISOString()

    const { data, error } = await supabase
      .from('rental_disputes')
      .insert({
        rental_id: input.rentalId,
        claimant_id: liveId,
        respondent_id: respondentId,
        category: input.category,
        claim_description: input.claimDescription,
        claim_amount_cents: input.claimAmountCents,
        claim_evidence_urls: urls,
        claim_evidence_metadata: toEvidenceJson(metadata),
        status: 'awaiting_response',
        response_deadline: responseDeadline,
      })
      .select('*')
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return withSignedEvidence(mapDispute(data))
  }

  async respondToDispute(userId: string, input: RespondToDisputeInput): Promise<RentalDispute> {
    // Only the respondent may answer, and respondent_id is RLS-checked against
    // auth.uid(). Resolve the LIVE identity so the ownership guard, the evidence
    // folder, and the atomic UPDATE guard all key off the real session id.
    const liveId = await requireLiveUserId()
    void userId

    const { data: existing, error: loadErr } = await supabase
      .from('rental_disputes')
      .select('id, rental_id, respondent_id, status, responded_at')
      .eq('id', input.disputeId)
      .single()

    if (loadErr || !existing) throw ServiceError.notFound('Dispute')
    if (existing.respondent_id !== liveId) throw ServiceError.unauthorized()
    if (existing.status !== 'awaiting_response' && existing.status !== 'open') {
      throw ServiceError.validation('Dispute is not awaiting a response', {
        currentStatus: existing.status,
      })
    }
    if (existing.responded_at) {
      throw ServiceError.validation('Already responded to this dispute')
    }

    const { urls, metadata } = await uploadEvidenceWithMetadata(
      input.evidence,
      `${liveId}/${existing.rental_id}`,
    )

    const { data, error } = await supabase
      .from('rental_disputes')
      .update({
        response_description: input.responseDescription,
        response_evidence_urls: urls,
        response_evidence_metadata: toEvidenceJson(metadata),
        responded_at: new Date().toISOString(),
        status: 'under_review',
      })
      .eq('id', input.disputeId)
      .eq('respondent_id', liveId) // atomic guard
      .is('responded_at', null) // prevents double-respond races
      .select('*')
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return withSignedEvidence(mapDispute(data))
  }

  async getDispute(disputeId: string): Promise<RentalDispute> {
    const { data, error } = await supabase
      .from('rental_disputes')
      .select('*')
      .eq('id', disputeId)
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return withSignedEvidence(mapDispute(data))
  }

  async listMyDisputes(userId: string): Promise<RentalDispute[]> {
    const { data, error } = await supabase
      .from('rental_disputes')
      .select('*')
      .or(`claimant_id.eq.${userId},respondent_id.eq.${userId}`)
      .order('created_at', { ascending: false })

    if (error) throw ServiceError.fromSupabaseError(error)
    return Promise.all((data ?? []).map((row) => withSignedEvidence(mapDispute(row))))
  }

  async listForRental(rentalId: string): Promise<RentalDispute[]> {
    const { data, error } = await supabase
      .from('rental_disputes')
      .select('*')
      .eq('rental_id', rentalId)
      .order('created_at', { ascending: false })

    if (error) throw ServiceError.fromSupabaseError(error)
    return Promise.all((data ?? []).map((row) => withSignedEvidence(mapDispute(row))))
  }
}

export const disputeService = new DisputeServiceImpl()
